"use client";
import { useReducedMotion } from "motion/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useOptionalCarouselAutoplayContext } from "./CarouselAutoplayContext.js";
const useCarouselAutoplayWithContext = (emblaApi, options)=>{
    const { syncWithGlobalAutoplay = false, carouselId } = options;
    const [autoplayIsPlaying, setAutoplayIsPlaying] = useState(false);
    const prefersReducedMotion = useReducedMotion() ?? false;
    const [autoplayIsManuallyPaused, setAutoplayIsManuallyPaused] = useState(prefersReducedMotion);
    const context = useOptionalCarouselAutoplayContext();
    const registeredRef = useRef(false);
    const onAutoplayButtonClick = useCallback((callback)=>{
        const autoplay = emblaApi?.plugins()?.autoplay;
        if (!autoplay) return void callback();
        autoplay.reset();
        callback();
    }, [
        emblaApi
    ]);
    const pauseCarousel = useCallback(()=>{
        const autoplay = emblaApi?.plugins()?.autoplay;
        if (autoplay) autoplay.stop();
    }, [
        emblaApi
    ]);
    const resumeCarousel = useCallback(()=>{
        const autoplay = emblaApi?.plugins()?.autoplay;
        if (autoplay) autoplay.play();
    }, [
        emblaApi
    ]);
    const isCarouselPlaying = useCallback(()=>{
        const autoplay = emblaApi?.plugins()?.autoplay;
        return autoplay ? autoplay.isPlaying() : false;
    }, [
        emblaApi
    ]);
    const handleGlobalAutoplayPause = useCallback(()=>{
        if (syncWithGlobalAutoplay && context) context.pauseAllCarousels();
    }, [
        syncWithGlobalAutoplay,
        context
    ]);
    const handleGlobalAutoplayResume = useCallback(()=>{
        if (syncWithGlobalAutoplay && context) context.resumeAllCarousels();
    }, [
        syncWithGlobalAutoplay,
        context
    ]);
    const toggleAutoplay = useCallback(()=>{
        const autoplay = emblaApi?.plugins()?.autoplay;
        if (!autoplay) return;
        if (prefersReducedMotion) {
            const newPausedState = !autoplayIsManuallyPaused;
            setAutoplayIsManuallyPaused(newPausedState);
            if (newPausedState) {
                autoplay.stop();
                handleGlobalAutoplayPause();
            } else {
                autoplay.reset();
                autoplay.play();
                handleGlobalAutoplayResume();
            }
        } else {
            const wasPlaying = autoplay.isPlaying();
            if (wasPlaying) {
                autoplay.stop();
                setAutoplayIsManuallyPaused(true);
                handleGlobalAutoplayPause();
            } else {
                setAutoplayIsManuallyPaused(false);
                autoplay.reset();
                autoplay.play();
                handleGlobalAutoplayResume();
            }
        }
    }, [
        emblaApi,
        prefersReducedMotion,
        autoplayIsManuallyPaused,
        handleGlobalAutoplayPause,
        handleGlobalAutoplayResume
    ]);
    const handleAutoplayToggleClick = useCallback((event)=>{
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        toggleAutoplay();
    }, [
        toggleAutoplay
    ]);
    const manuallyPauseAutoplay = useCallback(()=>{
        const autoplay = emblaApi?.plugins()?.autoplay;
        if (autoplay && autoplay.isPlaying()) {
            autoplay.stop();
            setAutoplayIsManuallyPaused(true);
        }
    }, [
        emblaApi
    ]);
    useEffect(()=>{
        if (!emblaApi || !prefersReducedMotion) return;
        const autoplay = emblaApi?.plugins()?.autoplay;
        if (!autoplay) return;
        if (autoplay.isPlaying() && autoplayIsManuallyPaused) autoplay.stop();
    }, [
        emblaApi,
        prefersReducedMotion,
        autoplayIsManuallyPaused
    ]);
    useEffect(()=>{
        if (!emblaApi || !prefersReducedMotion) return;
        const autoplay = emblaApi?.plugins()?.autoplay;
        if (!autoplay) return;
        if (autoplay.isPlaying() && autoplayIsManuallyPaused) autoplay.stop();
    }, [
        emblaApi,
        prefersReducedMotion,
        autoplayIsManuallyPaused
    ]);
    useEffect(()=>{
        if (!syncWithGlobalAutoplay || !context || !emblaApi || registeredRef.current) return;
        const controls = {
            emblaApi,
            pause: pauseCarousel,
            resume: resumeCarousel,
            isPlaying: isCarouselPlaying
        };
        context.registerCarousel(carouselId, controls);
        registeredRef.current = true;
        return ()=>{
            context.unregisterCarousel(carouselId);
            registeredRef.current = false;
        };
    }, [
        syncWithGlobalAutoplay,
        context,
        emblaApi,
        carouselId,
        pauseCarousel,
        resumeCarousel,
        isCarouselPlaying
    ]);
    useEffect(()=>{
        if (!emblaApi) return;
        const autoplay = emblaApi?.plugins()?.autoplay;
        if (!autoplay) return;
        const updateState = ()=>{
            const isPlaying = autoplay.isPlaying();
            setAutoplayIsPlaying(isPlaying);
        };
        updateState();
        const onPlay = ()=>{
            setAutoplayIsPlaying(true);
        };
        const onStop = ()=>{
            setAutoplayIsPlaying(false);
        };
        emblaApi.on('autoplay:play', onPlay).on('autoplay:stop', onStop).on('reInit', updateState);
        return ()=>{
            emblaApi.off('autoplay:play', onPlay).off('autoplay:stop', onStop);
        };
    }, [
        emblaApi
    ]);
    const effectiveAutoplayIsPlaying = prefersReducedMotion ? !autoplayIsManuallyPaused : autoplayIsPlaying;
    return {
        autoplayIsPlaying: effectiveAutoplayIsPlaying,
        autoplayIsManuallyPaused,
        toggleAutoplay: handleAutoplayToggleClick,
        onAutoplayButtonClick,
        manuallyPauseAutoplay
    };
};
export { useCarouselAutoplayWithContext };
